/**
 * Adaptive `refetchInterval` for TanStack Query pollers: fast (5s) while the
 * fetched content is actually changing, backing off to 10s then 30s once it
 * stabilizes.
 *
 * WHY this exists (incident perf prod 2026-08-26) : les versions inline de ce
 * calcul basaient le backoff sur `query.state.dataUpdatedAt` — l'horodatage du
 * dernier FETCH réussi. Or c'est précisément le fetch que le polling
 * déclenche : chaque poll remettait le chronomètre à zéro, l'écart ne
 * dépassait jamais l'intervalle lui-même, la branche « < 1 minute » gagnait
 * toujours et tous les consommateurs pollaient à 5s pour toujours. Mesuré en
 * prod : ~40 % du volume de requêtes de hector-backend venait de ces pollers
 * (+ leurs préflights CORS), avec un backend `standard` 1 vCPU saturé.
 *
 * Le correctif indexe le backoff sur le dernier CHANGEMENT de contenu : le
 * structural sharing de TanStack Query (activé par défaut, ne pas passer
 * `structuralSharing: false` sur les queries consommatrices) conserve la même
 * référence `query.state.data` quand un refetch renvoie un contenu
 * deep-equal. Une référence nouvelle === un vrai changement de données.
 *
 * L'état est mémorisé dans une WeakMap clé-ée sur l'objet Query du cache
 * (stable par queryKey, GC-é avec la query) : le helper reste une fonction
 * pure à brancher directement sur `refetchInterval`, sans ref à porter par
 * chaque hook.
 */

import { isTerminalHttpError } from '@/lib/errors/isTerminalHttpError';

/** Sous-ensemble structurel de l'objet Query reçu par `refetchInterval`. */
interface QueryLike {
  state: { data: unknown; error?: unknown };
}

const lastChangeByQuery = new WeakMap<QueryLike, { data: unknown; at: number }>();

/**
 * À brancher tel quel : `refetchInterval: adaptivePollInterval`.
 *
 * Paliers : contenu changé il y a < 1 min → 5s ; < 5 min → 10s ; au-delà →
 * 30s. Garder `refetchIntervalInBackground: false` sur les queries
 * consommatrices pour ne pas poller les onglets en arrière-plan.
 *
 * Une erreur définitive (401, 403, 404) arrête le polling : le refus ne passera
 * pas tout seul, et les données gardées en cache ne doivent pas le relancer.
 */
export function adaptivePollInterval(query: QueryLike): number | false {
  if (isTerminalHttpError(query.state.error)) return false;

  const data = query.state.data;
  if (data === undefined) {
    return 5_000; // rien encore fetché — polling rapide initial
  }

  const now = Date.now();
  const entry = lastChangeByQuery.get(query);
  if (!entry || entry.data !== data) {
    lastChangeByQuery.set(query, { data, at: now });
    return 5_000; // le contenu vient de changer — rester réactif
  }

  const elapsedMinutes = (now - entry.at) / 60_000;
  if (elapsedMinutes < 1) return 5_000;
  if (elapsedMinutes < 5) return 10_000;
  return 30_000;
}
